const blockers = new Set();
const blockerListeners = new Set();
const SHOW_DELAY_MS = 300;
const LOG_PREFIX = "[TutorialOverlay]";

function notifyBlockerListeners() {
  for (const fn of blockerListeners) fn();
}

export function setTutorialBlocker(id, blocked) {
  const wasBlocked = blockers.has(id);
  if (blocked && !wasBlocked) {
    blockers.add(id);
    notifyBlockerListeners();
  } else if (!blocked && wasBlocked) {
    blockers.delete(id);
    notifyBlockerListeners();
  }
}

export function isTutorialBlocked() {
  return blockers.size > 0;
}

export function getTutorialBlockers() {
  return [...blockers];
}

function onTutorialBlockersChange(fn) {
  blockerListeners.add(fn);
  return () => blockerListeners.delete(fn);
}

function syncBlockers(context, formDismissed) {
  setTutorialBlocker("intro", context.showIntroPopupVisible);
  setTutorialBlocker(
    "form",
    !!(context.formEnabled && context.visibleUi && !formDismissed),
  );
}

function buildLogContext(context) {
  return {
    ...context,
    tutorialBlockers: getTutorialBlockers(),
    audioInitOverlayPresent: !!document.getElementById("audio-init-overlay"),
  };
}

/**
 * Manages tutorial overlay scheduling, deferral blockers, and debug logging.
 * Tour.svelte keeps a reactive `showTutorialOverlay` bound via onShowChange.
 */
export function createTutorialOverlay({ onShowChange, getContext }) {
  /** @type {null | true | false} null = not yet shown */
  let showState = null;
  let showTimeoutId = null;
  let formDismissed = false;
  let unsubscribeBlockers = null;

  function log(message, extra = {}) {
    console.log(LOG_PREFIX, message, buildLogContext({ ...getContext(), ...extra }));
  }

  function clearShowTimeout() {
    if (!showTimeoutId) return;
    clearTimeout(showTimeoutId);
    showTimeoutId = null;
  }

  function setShowState(next) {
    showState = next;
    onShowChange(next);
  }

  function showWhenReady() {
    const latestContext = getContext();
    syncBlockers(latestContext, formDismissed);
    if (isTutorialBlocked()) {
      log("show aborted", { blockers: getTutorialBlockers() });
      return;
    }
    setShowState(true);
    log("show");
  }

  function maybeSchedule() {
    const context = getContext();
    if (!context.visibleUi || !context.expandedTour) return;
    if (showState !== null || showTimeoutId) return;

    syncBlockers(context, formDismissed);
    if (isTutorialBlocked()) {
      log("deferred", { blockers: getTutorialBlockers() });
      return;
    }

    if (context.hasLittlePlanetIntro) {
      log("show immediately (little planet intro)");
      showWhenReady();
      return;
    }

    log(`scheduling show in ${SHOW_DELAY_MS}ms`);
    showTimeoutId = setTimeout(() => {
      showTimeoutId = null;
      showWhenReady();
    }, SHOW_DELAY_MS);
  }

  function onBlockersChanged() {
    if (isTutorialBlocked() && showTimeoutId) {
      clearShowTimeout();
      log("cancelled pending show", { blockers: getTutorialBlockers() });
    }
    maybeSchedule();
  }

  return {
    mount() {
      unsubscribeBlockers = onTutorialBlockersChange(onBlockersChanged);
    },

    destroy() {
      unsubscribeBlockers?.();
      clearShowTimeout();
    },

    /** Call when reactive deps change (intro visibility, UI visibility, ada param). */
    syncBlockers() {
      syncBlockers(getContext(), formDismissed);
    },

    /** Call from showUi() after visibleUi becomes true. */
    onUiVisible() {
      syncBlockers(getContext(), formDismissed);
      maybeSchedule();
    },

    /** Call when inquiry form is closed for the first time. */
    onFormDismissed() {
      formDismissed = true;
      syncBlockers(getContext(), formDismissed);
    },

    dismiss(reason = "unknown") {
      log("dismiss", { reason, wasVisible: showState === true });
      clearShowTimeout();
      setShowState(false);
    },

    isShowing() {
      return showState === true;
    },
  };
}
